import { useState } from 'react'
import { ChatBubbleLeftEllipsisIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline'
import type { ThreadRead } from '../../client'
import { relativeTime } from '../../lib/format'
import RenameDialog from './RenameDialog'
import { useChat } from '../../providers/chat/context'

export default function ThreadList({ onSelect }: { onSelect?: () => void }) {
  const { threads, activeId, openThread, archive, hasMoreThreads, loadingMoreThreads, loadMoreThreads } =
    useChat()
  const [renaming, setRenaming] = useState<ThreadRead | null>(null)

  if (threads.length === 0) {
    return <p className="px-3 py-3 text-[11px] text-stone-400">No conversations yet.</p>
  }

  return (
    <div className="flex flex-col">
      {threads.map((thread) => (
        <div
          key={thread.id}
          className={`group flex items-start gap-2 rounded-md px-3 py-2 transition ${
            thread.id === activeId ? 'bg-purple-50 text-purple-900' : 'text-stone-700 hover:bg-stone-50'
          }`}
        >
          <button
            type="button"
            onClick={() => {
              void openThread(thread.id)
              onSelect?.()
            }}
            className="flex min-w-0 flex-1 items-start gap-2 text-left"
          >
            <ChatBubbleLeftEllipsisIcon className="mt-0.5 size-4 shrink-0 opacity-70" />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium">
                {thread.title || 'New conversation'}
              </span>
              <span className="mt-0.5 block text-[10px] text-stone-400">
                {relativeTime(thread.updated_at)}
              </span>
            </span>
          </button>
          <div className="flex shrink-0 items-center gap-0.5 opacity-0 transition group-hover:opacity-100 focus-within:opacity-100 pointer-coarse:opacity-100">
            <button
              type="button"
              aria-label="Rename"
              onClick={() => setRenaming(thread)}
              className="rounded p-1 hover:bg-white"
            >
              <PencilSquareIcon className="size-3.5 text-stone-500" />
            </button>
            <button
              type="button"
              aria-label="Delete"
              onClick={() => void archive(thread.id)}
              className="rounded p-1 hover:bg-white"
            >
              <TrashIcon className="size-3.5 text-stone-500" />
            </button>
          </div>
        </div>
      ))}

      {renaming && <RenameDialog thread={renaming} onClose={() => setRenaming(null)} />}

      {hasMoreThreads && (
        <button
          type="button"
          disabled={loadingMoreThreads}
          onClick={() => void loadMoreThreads()}
          className="mx-3 mt-1 rounded-md py-1.5 text-[11px] text-purple-700 transition hover:bg-purple-50 disabled:opacity-50"
        >
          {loadingMoreThreads ? 'Loading…' : 'Load more'}
        </button>
      )}
    </div>
  )
}
